import {
  Button, Card, ErrorState, Field, LoadingState, Notice, Permissions, Row, Stack, Textarea,
  useApi, useI18n, usePermission,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * The legal hold that stops the retention purge of a child's records (Q37).
 *
 * Whoever opens the child's page here sees whether a hold stands, so nobody withdraws or tidies a child under one
 * without knowing. Only the owner places or lifts it (`children.legal_hold`), and placing it needs a reason.
 */
export function LegalHoldCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const canRead = usePermission(Permissions.children.update);
  const canDecide = usePermission(Permissions.children.legalHold);
  const [placing, setPlacing] = useState(false);
  const [confirmingLift, setConfirmingLift] = useState(false);

  const key = ['child', childId, 'legal-hold'];
  const hold = useQuery({ queryKey: key, queryFn: () => api.legalHold.get(childId), enabled: canRead });

  const lift = useMutation({
    mutationFn: () => api.legalHold.lift(childId),
    onSuccess: () => {
      setConfirmingLift(false);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  if (!canRead) return null;

  const since = hold.data?.since ?? null;
  const held = since !== null;
  const unheld = hold.data !== undefined && !held;

  return (
    <Card
      title={t('legalHold.title')}
      hint={t('legalHold.hint')}
      action={canDecide && unheld && !placing
        ? <Button variant="secondary" onClick={() => setPlacing(true)}>{t('legalHold.place')}</Button>
        : undefined}
    >
      <Stack>
        {hold.isPending && <LoadingState />}
        {hold.isError && <ErrorState error={hold.error} onRetry={() => void hold.refetch()} />}
        {lift.isError && <ErrorState error={lift.error} />}

        {unheld && !placing && <p className="dg-card__hint">{t('legalHold.none')}</p>}

        {hold.data && since !== null && (
          <Notice tone="warning">
            <Stack>
              <strong>
                {hold.data.placedBy
                  ? t('legalHold.held', { date: formatDate(since), name: hold.data.placedBy })
                  : t('legalHold.held.unknown', { date: formatDate(since) })}
              </strong>
              <span>{hold.data.reason}</span>
            </Stack>
          </Notice>
        )}

        {canDecide && held && !confirmingLift && (
          <Row>
            <Button variant="ghost" onClick={() => setConfirmingLift(true)}>{t('legalHold.lift')}</Button>
          </Row>
        )}

        {confirmingLift && (
          <Stack>
            <p>{t('legalHold.lift.confirm')}</p>
            <Row>
              <Button variant="danger" busy={lift.isPending} onClick={() => lift.mutate()}>{t('legalHold.lift')}</Button>
              <Button variant="ghost" onClick={() => setConfirmingLift(false)}>{t('action.cancel')}</Button>
            </Row>
          </Stack>
        )}

        {placing && (
          <PlaceHoldForm
            childId={childId}
            onCancel={() => setPlacing(false)}
            onPlaced={() => { setPlacing(false); void queryClient.invalidateQueries({ queryKey: key }); }}
          />
        )}
      </Stack>
    </Card>
  );
}

function PlaceHoldForm({ childId, onCancel, onPlaced }: {
  childId: number;
  onCancel: () => void;
  onPlaced: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();

  const place = useMutation({ mutationFn: () => api.legalHold.place(childId, reason.trim()), onSuccess: onPlaced });

  function submit(event: FormEvent) {
    event.preventDefault();
    const missing = reason.trim() ? undefined : t('legalHold.reason.required');
    setError(missing);
    if (!missing) place.mutate();
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        {place.isError && <ErrorState error={place.error} />}
        <Field label={t('legalHold.reason')} hint={t('legalHold.reason.hint')} error={error} required>
          {(props) => <Textarea {...props} value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />}
        </Field>
        <Row>
          <Button type="submit" busy={place.isPending} busyLabel={t('action.saving')}>{t('legalHold.place')}</Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}
